import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LocaleProvider, useLocale, SubscriptionProvider, Paywall, useOrganizationId, NotFoundPage } from '@platform/core';
import { ThemeProvider } from '@platform/ui-kit';
import { SimpleAuthProvider, useSimpleAuth } from './contexts/SimpleAuthContext';
import { PlanningProvider } from './contexts/PlanningContext';
import Layout from './components/Layout';
import PlansPage from './pages/PlansPage';
import PlanDetailPage from './pages/PlanDetailPage';
import CalendarPage from './pages/CalendarPage';
import ActionItemsPage from './pages/ActionItemsPage';
import LoginPage from './pages/LoginPage';
import VisualizationsPage from './pages/VisualizationsPage';
import ReportsPage from './pages/ReportsPage';
import ActionStatesPage from './pages/ActionStatesPage';
import AlertsPage from './pages/AlertsPage';
import ManageCompanyPage from './pages/ManageCompanyPage';
import CompaniesPage from './pages/CompaniesPage';
import HatsPage from './pages/HatsPage';
import POVsPage from './pages/POVsPage';
import EmployeesGridPage from './pages/EmployeesGridPage';
import MetricsPage from './pages/MetricsPage';
import SalesReportPage from './pages/SalesReportPage';
import DashboardsPage from './pages/DashboardsPage';
import PPAPage from './pages/PPAPage';
import BizPotPage from './pages/BizPotPage';

// Sets dir and lang on <html> element so entire page respects RTL/LTR
function DirectionSetter({ children }: { children: React.ReactNode }) {
  const { direction, locale } = useLocale();

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
  }, [direction, locale]);

  return <>{children}</>;
}

function LoadingScreen() {
  const { t } = useLocale();
  return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>{t('general.loading')}</p>
    </div>
  );
}

function AppRoutes() {
  const { user, isLoading } = useSimpleAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <PlanningProvider>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/plans" replace />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="plans/:id" element={<PlanDetailPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="action-items" element={<ActionItemsPage />} />
          <Route path="visualizations" element={<VisualizationsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="action-states" element={<ActionStatesPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="manage-company" element={<ManageCompanyPage />} />
          <Route path="companies" element={<CompaniesPage />} />
          <Route path="hats" element={<HatsPage />} />
          <Route path="povs" element={<POVsPage />} />
          <Route path="employees" element={<EmployeesGridPage />} />
          <Route path="metrics" element={<MetricsPage />} />
          <Route path="sales-report" element={<SalesReportPage />} />
          <Route path="dashboards" element={<DashboardsPage />} />
          <Route path="ppa" element={<PPAPage />} />
          <Route path="bizpot" element={<BizPotPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage onGoHome={() => window.location.href = '/plans'} />} />
      </Routes>
    </PlanningProvider>
  );
}

function AppRoutesWithPaywall() {
  const { organizationId, isAdmin } = useOrganizationId();

  return (
    <SubscriptionProvider organizationId={organizationId}>
      <Paywall isAdmin={isAdmin}>
        <AppRoutes />
      </Paywall>
    </SubscriptionProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LocaleProvider defaultLocale="he">
        <DirectionSetter>
          <SimpleAuthProvider>
            <BrowserRouter>
              <AppRoutesWithPaywall />
            </BrowserRouter>
          </SimpleAuthProvider>
        </DirectionSetter>
      </LocaleProvider>
    </ThemeProvider>
  );
}
